export const conditionField = (catalog, rule) =>
  (
    catalog?.condition_fields?.[
      ['patient', 'appointment_service'].includes(rule.source)
        ? rule.source
        : 'appointment'
    ] ||
    catalog?.fields ||
    []
  ).find(field => field.name === rule.field) || {};

export const conditionValue = (value, t) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return t(value ? 'yes' : 'no');
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    const interval = Object.hasOwn(value, 'amount')
      ? value.amount
      : `${value.from ?? '—'} – ${value.to ?? '—'}`;
    return value.unit
      ? `${interval ?? '—'} · ${t(value.unit)}`
      : String(interval);
  }
  return String(value);
};

export const conditionRuleValue = (catalog, rule, t) => {
  const field = conditionField(catalog, rule);
  if (field.editor_visible === false) return t('conditionEditor.legacyValue');
  if (field.references)
    return rule.value_label || t('conditionEditor.unknownValue');
  return (
    field.options?.find(item => item.value === rule.value)?.label ||
    conditionValue(rule.value, t)
  );
};

export const conditionMessages = {
  ru: {
    patient: 'Пациент',
    appointment_service: 'Товар записи на приём',
    appointment_reference_missing:
      'Не удалось определить запись на приём для проверки товаров.',
    between: 'в диапазоне (включая границы)',
    today: 'сегодня',
    on_date: 'в указанную дату',
    before_date: 'раньше даты',
    after_date: 'позже даты',
    date_between: 'между датами (включительно)',
    future_between: 'наступит через — диапазон',
    past_between: 'прошло — диапазон',
    future_gt: 'наступит более чем через',
    future_lt: 'наступит менее чем через',
    past_gt: 'прошло более',
    past_lt: 'прошло менее',
    condition_context_invalid:
      'Выберите сущность: пациенты, записи на приём или товары записей на приём.',
    previous_context_unavailable:
      'Предыдущие значения доступны только при изменении или удалении приёма.',
    condition_date_invalid:
      'Дата в исходных данных имеет неизвестный формат. Отправка остановлена.',
    conditionEditor: {
      chooseParameter: 'Выберите параметр',
      searchParameters: 'Поиск по параметрам',
      noParameters: 'Параметры не найдены',
      value: 'Значение',
      chooseValue: 'Выберите значение',
      unknownValue: 'Сохранённое значение — откройте список для уточнения',
      searchValues: 'Поиск по названию',
      noValues: 'Ничего не найдено. Проверьте поиск и выбранное подключение.',
      more: 'Показать ещё',
      mis: 'МИС {number}',
      textHint:
        'Введите текст или выберите подсказку из данных подключения. Список не ограничивает ввод.',
      loadingValues: 'Загрузка значений…',
      valuesError: 'Не удалось загрузить значения.',
      retry: 'Повторить',
      connectionRequired:
        'Сначала выберите подключение МИС в настройках запуска сценария.',
      legacyField: 'Служебное поле старого условия',
      legacyValue:
        'Существующее условие сохранено. Для изменения выберите понятное поле из списка.',
      collection: 'Проверка товаров',
      anyProduct: 'Есть товар, соответствующий условиям',
      noProduct: 'Нет товаров, соответствующих условиям',
      appointment_service: 'Товары записей на приём',
      appointment_serviceHint:
        'Товары текущей записи на приём. Все условия в группе И проверяются у одного товара, а не у разных строк.',
      addProductFixture: 'Добавить тестовый товар',
      productsFixture: 'Тестовые товары записи на приём',
      configure: 'Настроить условие',
      apply: 'Применить',
      draftHint:
        'Настройте проверку и примените изменения к блоку. Затем сохраните черновик сценария.',
      context: 'Сущность',
      appointment: 'Записи на приём',
      patient: 'Пациенты',
      appointmentHint:
        'Проверяется только приём, запустивший этот сценарий, а не любой приём пациента.',
      patientHint:
        'Актуальные данные пациента текущей записи. Саму запись и её товары проверяйте отдельными блоками.',
      changeContext:
        'Параметр из другого раздела заменит все условия этого блока. Для совместной проверки разных разделов используйте отдельные блоки. Продолжить?',
      source: 'Какие данные',
      field: 'Поле',
      timezone:
        'Даты проверяются в часовом поясе подключения МИС. Границы диапазона включены; «более» и «менее» — строгие сравнения.',
      relativeHint:
        'Интервал отсчитывается от момента проверки. Один день — 24 часа.',
      from: 'От',
      to: 'До',
    },
  },
  en: {
    patient: 'Patient',
    appointment_service: 'Appointment product',
    appointment_reference_missing:
      'The appointment could not be resolved for the product check.',
    between: 'between (inclusive)',
    today: 'today',
    on_date: 'on date',
    before_date: 'before date',
    after_date: 'after date',
    date_between: 'between dates (inclusive)',
    future_between: 'occurs in — range',
    past_between: 'elapsed — range',
    future_gt: 'occurs in more than',
    future_lt: 'occurs in less than',
    past_gt: 'elapsed more than',
    past_lt: 'elapsed less than',
    condition_context_invalid: 'Select a patient or appointment context.',
    previous_context_unavailable:
      'Previous values are available only for updated or deleted appointments.',
    condition_date_invalid:
      'The source date has an unsupported format. Sending has stopped.',
    conditionEditor: {
      chooseParameter: 'Choose a parameter',
      searchParameters: 'Search parameters',
      noParameters: 'No parameters found',
      value: 'Value',
      chooseValue: 'Choose a value',
      unknownValue: 'Saved value — open the list to resolve it',
      searchValues: 'Search by name',
      noValues: 'No results. Check the search and selected connection.',
      more: 'Show more',
      mis: 'MIS {number}',
      textHint:
        'Enter text or choose a suggestion from the connection data. Suggestions do not restrict input.',
      loadingValues: 'Loading values…',
      valuesError: 'Could not load values.',
      retry: 'Retry',
      connectionRequired:
        'Select a MIS connection in the workflow trigger settings first.',
      legacyField: 'Technical field from an existing condition',
      legacyValue:
        'The existing rule is preserved. Choose a business field to change it.',
      collection: 'Product check',
      anyProduct: 'A product matches the conditions',
      noProduct: 'No product matches the conditions',
      appointment_service: 'Appointment products',
      appointment_serviceHint:
        'Products of the current appointment. All AND rules must match the same product row.',
      addProductFixture: 'Add a test product',
      productsFixture: 'Test appointment products',
      configure: 'Configure condition',
      apply: 'Apply',
      draftHint:
        'Configure the check and apply changes to this block. Then save the workflow draft.',
      context: 'Entity',
      appointment: 'Appointments',
      patient: 'Patients',
      appointmentHint:
        'Checks the appointment that triggered this workflow, not any appointment belonging to the patient.',
      patientHint:
        'Current patient data, resolved within the connection using MIS keys. Check the appointment in a separate condition.',
      changeContext:
        'A parameter from another section replaces all conditions in this block. Use separate blocks to check different sections together. Continue?',
      source: 'Data source',
      field: 'Field',
      timezone:
        'Dates use the MIS connection timezone. Range boundaries are inclusive; more/less comparisons are strict.',
      relativeHint: 'Relative to evaluation time. One day means 24 hours.',
      from: 'From',
      to: 'To',
    },
  },
};
